import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { eachDayOfInterval, endOfMonth, startOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { PageShell } from '../../../components/leaves/PageShell';
import { Calendar } from 'lucide-react';
import AttendanceCalendar from './components/AttendanceCalendar';
import LeaveRequestPanel from './components/LeaveRequestPanel';
import WorkScheduleCalendar from './components/WorkScheduleCalendar';
import DetailedDayViewPanel from './components/DetailedDayViewPanel';
import { Skeleton } from '../../../components/ui/Skeleton';

export { WorkScheduleCalendar };

// Month-grid placeholder shown while attendance data is loading for the first time.
// Mirrors AttendanceCalendar's 7-col header + 6-week grid layout so the page
// doesn't reflow once data arrives.
const CalendarSkeleton = () => (
  <div className="w-full" aria-busy="true" aria-live="polite">
    <div className="flex items-center justify-between mb-6">
      <Skeleton className="h-8 w-8 rounded-lg" />
      <Skeleton className="h-6 w-40 rounded" />
      <Skeleton className="h-8 w-8 rounded-lg" />
    </div>
    <div className="grid grid-cols-7 gap-1">
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton key={`hdr-${i}`} className="h-4 w-10 mx-auto rounded" />
      ))}
      {Array.from({ length: 42 }).map((_, i) => (
        <Skeleton key={`cell-${i}`} className="h-12 w-full rounded-lg" />
      ))}
    </div>
    <div className="mt-6 pt-4 border-t border-border">
      <Skeleton className="h-3 w-16 mb-3 rounded" />
      <div className="flex flex-wrap gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`legend-${i}`} className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded-full" />
            <Skeleton className="h-3 w-16 rounded" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

import { Roles } from '../../../components/roles';
import { formatLocalDateParam } from '../../../utils/apiDate';
import { getTimesheetTableData } from '../../../api/Attendance/attendance';
import { useScheduledShifts } from '../../../hooks/useScheduling';
import { getMyCorrectionRequests } from '../../../api/Attendance/correctionRequests';
import { getUserManagers } from '../../../api/General/account';
import { getCompanyRules } from '../../../api/General/Company';

const AttendanceManagement = ({
  targetUserId: targetUserIdProp,
  targetUserFullName,
} = {}) => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  const targetUserIdFromRoute = params?.targetUserId;
  const targetUserFullNameFromRoute = (() => {
    const raw = params?.targetUserFullName;
    if (!raw) return undefined;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  })();

  const viewerUserId = useSelector((state) => state.user.userId);
  const viewerRole = useSelector((state) => state.user.role);
  const viewerCompanyId = useSelector((state) => state.user.company?.id);
  const rtl = useSelector((state) => state.user.rtl);

  const titleIconGapClass = rtl ? 'ml-2 mr-0' : 'mr-2 ml-0';
  const headerIconGapClass = rtl ? 'ml-4 mr-0' : 'mr-4 ml-0';
  const inlineSpaceClass = rtl ? 'space-x-2 space-x-reverse' : 'space-x-2';

  const targetUserId = targetUserIdProp || targetUserIdFromRoute || viewerUserId;
  const effectiveTargetUserFullName = targetUserFullName || targetUserFullNameFromRoute;
  const isSelf = !!targetUserId && !!viewerUserId && targetUserId === viewerUserId;

  const isHr = Roles.HR.includes(viewerRole);
  const isManagerRole = Roles.MANAGER.includes(viewerRole);
  const [isManagerOfTarget, setIsManagerOfTarget] = useState(false);

  useEffect(() => {
    if (!targetUserId || !viewerUserId) {
      setIsManagerOfTarget(false);
      return;
    }
    if (isSelf) {
      setIsManagerOfTarget(false);
      return;
    }
    if (!isManagerRole) {
      setIsManagerOfTarget(false);
      return;
    }

    const check = async () => {
      try {
        const res = await getUserManagers(targetUserId);
        const managers = res?.payload || [];
        const ok = managers.some((s) => String(s?.id) === String(viewerUserId));
        setIsManagerOfTarget(ok);
      } catch {
        setIsManagerOfTarget(false);
      }
    };

    check();
  }, [targetUserId, viewerUserId, isSelf, isManagerRole]);

  const canViewTarget = isSelf || isHr || isManagerOfTarget;
  const canActOnTarget = isSelf;

  const [monthDayKeys, setMonthDayKeys] = useState([]);
  const [summaryByDate, setSummaryByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [correctionsLoading, setCorrectionsLoading] = useState(false);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState(null);
  const [clockingsByDate, setClockingsByDate] = useState({});

  const [companyRules, setCompanyRules] = useState(null);

  const uiRole = (isHr || isManagerRole) ? 'manager' : 'employee';
  const isViewingByProp = !!targetUserIdProp;
  const isViewingByRoute = !!targetUserIdFromRoute;
  const title = (!isViewingByProp && !isViewingByRoute) || isSelf
    ? t('My Attendance')
    : t("{{name}}'s Attendance", { name: effectiveTargetUserFullName || t('User') });

  useEffect(() => {
    if (!viewerCompanyId) {
      setCompanyRules(null);
      return;
    }

    const load = async () => {
      try {
        const res = await getCompanyRules(viewerCompanyId);
        setCompanyRules(res?.payload || null);
      } catch {
        setCompanyRules(null);
      }
    };

    load();
  }, [viewerCompanyId]);
  
  useEffect(() => {
    if (!targetUserId) return;
    if (!canViewTarget) return;

    const fetchMonth = async () => {
      setMonthLoading(true);
      setMonthError(null);
      try {
        const from = startOfMonth(currentMonth);
        const to = endOfMonth(currentMonth);
        const fromParam = formatLocalDateParam(from);
        const toParam = formatLocalDateParam(to);

        const res = await getTimesheetTableData(targetUserId, fromParam, toParam);
        const payload = res?.payload;

        const days = eachDayOfInterval({ start: from, end: to });
        const summaries = payload?.daysSummaryView || [];
        const daysClockings = payload?.daysClockings || [];

        const nextClockingsByDate = {};
        const nextSummaryByDate = {};
        const nextMonthDayKeys = [];

        const formatHm = (value) => {
          if (!value) return null;
          const d = new Date(value);
          if (Number.isNaN(d.getTime())) return null;
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          return `${hh}:${mm}`;
        };

        const pickClock = (clockings, type, pickLast) => {
          const list = (clockings || []).filter((c) => c?.type === type);
          if (list.length === 0) return null;
          const item = pickLast ? list[list.length - 1] : list[0];
          return formatHm(item?.time);
        };

        days.forEach((dateObj, idx) => {
          const dateStr = formatLocalDateParam(dateObj);
          const summary = summaries[idx];
          const clockings = daysClockings[idx] || [];

          nextMonthDayKeys.push(dateStr);
          nextSummaryByDate[dateStr] = summary;

          nextClockingsByDate[dateStr] = clockings;
        });

        setMonthDayKeys(nextMonthDayKeys);
        setSummaryByDate(nextSummaryByDate);
        setClockingsByDate(nextClockingsByDate);
      } catch (e) {
        setMonthError(e?.message || 'Failed to load attendance data');
      } finally {
        setMonthLoading(false);
      }
    };

    fetchMonth();
  }, [targetUserId, canViewTarget, currentMonth]);

  const monthRange = useMemo(() => {
    const from = startOfMonth(currentMonth);
    const to = endOfMonth(currentMonth);
    return eachDayOfInterval({ start: from, end: to });
  }, [currentMonth]);

  const { shifts: monthScheduledShifts } = useScheduledShifts(monthRange, {
    userId: canViewTarget ? targetUserId : (viewerUserId || undefined),
  });

  const attendanceData = useMemo(() => {
    const todayKey = formatLocalDateParam(new Date());
    const rules = companyRules || {};

    const absentThresholdPercent = Number.isFinite(Number(rules?.scheduledShiftAbsentThresholdPercent))
      ? Number(rules.scheduledShiftAbsentThresholdPercent)
      : 10;
    const halfDayThresholdPercent = Number.isFinite(Number(rules?.scheduledShiftHalfDayThresholdPercent))
      ? Number(rules.scheduledShiftHalfDayThresholdPercent)
      : 50;
    const lateThresholdMinutes = Number.isFinite(Number(rules?.scheduledShiftLateClockInThresholdMinutes))
      ? Number(rules.scheduledShiftLateClockInThresholdMinutes)
      : 5;
    const earlyThresholdMinutes = Number.isFinite(Number(rules?.scheduledShiftEarlyClockOutThresholdMinutes))
      ? Number(rules.scheduledShiftEarlyClockOutThresholdMinutes)
      : 5;

    const toMinutes = (hhmm) => {
      if (!hhmm || typeof hhmm !== 'string') return null;
      const [hh, mm] = hhmm.split(':');
      const h = Number(hh);
      const m = Number(mm);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };

    const parseDurationToMinutes = (duration) => {
      if (!duration || typeof duration !== 'string') return 0;
      const parts = duration.split(':');
      if (parts.length < 2) return 0;
      const h = Number(parts[0]);
      const m = Number(parts[1]);
      const s = Number(parts[2] ?? 0);
      if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) return 0;
      return h * 60 + m + Math.round(s / 60);
    };

    const pickClock = (clockings, type, pickLast) => {
      const list = (clockings || []).filter((c) => c?.type === type);
      if (list.length === 0) return null;
      const item = pickLast ? list[list.length - 1] : list[0];
      const value = item?.time;
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    };

    // Consolidate scheduled shifts by date (earliest start, latest end)
    const schedByDate = {};
    (monthScheduledShifts || []).forEach((s) => {
      if (!s?.date) return;
      const start = toMinutes(s.startTime);
      const end = toMinutes(s.endTime);
      if (start == null || end == null) return;

      const current = schedByDate[s.date];
      const next = {
        startMinutes: current ? Math.min(current.startMinutes, start) : start,
        endMinutes: current ? Math.max(current.endMinutes, end) : end,
      };

      // handle shifts that cross midnight (rare, but safe)
      const rawDuration = next.endMinutes - next.startMinutes;
      next.durationMinutes = rawDuration >= 0 ? rawDuration : (24 * 60 + rawDuration);
      schedByDate[s.date] = next;
    });

    const mapDayStatusToUi = (dayStatus) => {
      switch (dayStatus) {
        case 'Present':
          return 'present';
        case 'HalfDayLeave':
          return 'half-day';
        case 'Leave':
          return 'leave';
        case 'SickLeave':
          return 'sick-leave';
        case 'Holiday':
        case 'PublicHoliday':
          return 'holiday';
        default:
          return null;
      }
    };

    const clampPercent = (value, fallback) => {
      const v = Number(value);
      if (!Number.isFinite(v)) return fallback;
      return Math.min(100, Math.max(0, v));
    };

    const absentPct = clampPercent(absentThresholdPercent, 10);
    const halfDayPct = clampPercent(halfDayThresholdPercent, 50);
    const absentRatio = absentPct / 100;
    const halfDayRatio = halfDayPct / 100;

    const rows = [];
    (monthDayKeys || []).forEach((dateStr) => {
      const summary = summaryByDate?.[dateStr];
      const clockings = clockingsByDate?.[dateStr] || [];
      const sched = schedByDate?.[dateStr];
      const isFuture = !!dateStr && dateStr > todayKey;

      const workingMinutes = parseDurationToMinutes(summary?.workingTime);
      const hoursWorked = workingMinutes / 60;

      let status = mapDayStatusToUi(summary?.dayStatus);
      const hasClockings = clockings.length > 0;

      if (isFuture) {
        // Future days:
        // - Always show leave/sick/holiday (past & future).
        // - Never show absent/late/early-clock-out for future days.
        // - If there is a scheduled shift on a future day, show present.
        if (!status) {
          if (hasClockings) {
            status = 'present';
          } else if (sched) {
            status = 'present';
          } else {
            return;
          }
        } else if (status === 'absent' || status === 'late' || status === 'early-leave') {
          status = (sched || hasClockings) ? 'present' : null;
          if (!status) return;
        }
      } else {
        // If no explicit status from backend, infer based on schedule + clockings
        if (!status)
        {
          if (hasClockings)
          {
            status = 'present';
          }
          else if (sched)
          {
            status = 'absent';
          }
        }

        if (!status)
        {
          return;
        }

        // If a scheduled shift exists and we have working time, refine absent/half-day
        if (sched && sched.durationMinutes > 0)
        {
          const ratio = workingMinutes / sched.durationMinutes;
          if (ratio <= absentRatio)
          {
            status = 'absent';
          }
          else if (ratio < halfDayRatio)
          {
            status = 'half-day';
          }
        }

        // Late / early leave indicators (only when not absent)
        if (sched && status !== 'absent')
        {
          const checkIn = pickClock(clockings, 'ClockIn', false);
          const checkOut = pickClock(clockings, 'ClockOut', true);
          let checkInM = toMinutes(checkIn);
          let checkOutM = toMinutes(checkOut);

          const startM = sched.startMinutes;
          let endM = sched.endMinutes;
          const crossesMidnight = startM > endM;

          if (crossesMidnight) {
            // Normalize: shift times on the "next day" side (0..endM) get +24h
            // so all comparisons are on a continuous 0..2880 scale
            const rawEndM = endM;
            endM += 24 * 60;
            if (checkInM != null && checkInM <= rawEndM) {
              checkInM += 24 * 60;
            }
            if (checkOutM != null && checkOutM <= rawEndM) {
              checkOutM += 24 * 60;
            }
          }

          if (checkInM != null && checkInM > (startM + Math.max(0, lateThresholdMinutes)))
          {
            status = 'late';
          }
          else if (checkOutM != null && checkOutM < (endM - Math.max(0, earlyThresholdMinutes)))
          {
            status = 'early-leave';
          }
        }
      }

      rows.push({
        date: dateStr,
        status,
        hoursWorked: Number(hoursWorked.toFixed(2)),
        checkIn: pickClock(clockings, 'ClockIn', false),
        checkOut: pickClock(clockings, 'ClockOut', true),
        breakIn: pickClock(clockings, 'BreakIn', false),
        breakOut: pickClock(clockings, 'BreakOut', true),
        dayStatus: summary?.dayStatus,
      });
    });

    return rows;
  }, [monthDayKeys, summaryByDate, clockingsByDate, monthScheduledShifts, companyRules, canViewTarget, targetUserId, viewerUserId]);

  useEffect(() => {
    if (!viewerUserId) return;
    if (!isSelf) {
      setCorrectionRequests([]);
      return;
    }

    const fetchMyCorrections = async () => {
      setCorrectionsLoading(true);
      try {
        const res = await getMyCorrectionRequests(viewerUserId);
        setCorrectionRequests(res?.payload || []);
      } catch {
        setCorrectionRequests([]);
      } finally {
        setCorrectionsLoading(false);
      }
    };

    fetchMyCorrections();
  }, [viewerUserId, isSelf]);

  const handleCorrectionRequest = () => {
    const prefillDate = selectedDate || formatLocalDateParam(new Date());
    navigate('/attendance/correction-requests', {
      state: {
        prefill: {
          date: prefillDate,
        },
      },
    });
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleExportReport = () => {
    navigate('/attendance/reports/clockings');
  };

  const stats = useMemo(() => {
    const totalDays = attendanceData?.length || 0;
    const presentDays = attendanceData?.filter(day => ['present', 'late', 'early-leave'].includes(day?.status))?.length || 0;
    const absentDays = attendanceData?.filter(day => ['absent', 'leave', 'sick-leave', 'holiday'].includes(day?.status))?.length || 0;
    const halfDays = attendanceData?.filter(day => day?.status === 'half-day')?.length || 0;
    const attendanceRate = totalDays > 0 ? ((presentDays + halfDays * 0.5) / totalDays * 100)?.toFixed(1) : 0;
    return { totalDays, presentDays, absentDays, halfDays, attendanceRate };
  }, [attendanceData]);

  const getCorrectionRequestStatusLabel = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'approved') return t('Approved');
    if (s === 'rejected') return t('Rejected');
    if (s === 'pending') return t('Pending');
    return status ? t(String(status)) : '';
  };

  const selectedDateDetails = useMemo(() => {
    if (!selectedDate) return null;
    return attendanceData?.find(day => day?.date === selectedDate) || null;
  }, [attendanceData, selectedDate]);

  const selectedDayClockingsByType = useMemo(() => {
    const empty = {
      ClockIn: [],
      ClockOut: [],
      BreakIn: [],
      BreakOut: [],
    };

    if (!selectedDate) return empty;

    const clockings = clockingsByDate?.[selectedDate] || [];
    const knownTypes = new Set(['ClockIn', 'ClockOut', 'BreakIn', 'BreakOut']);

    const normalized = (clockings || [])
      .filter((c) => knownTypes.has(c?.type) && c?.time)
      .map((c) => {
        const d = new Date(c.time);
        return {
          type: c.type,
          date: d,
          valid: !Number.isNaN(d.getTime()),
        };
      })
      .filter((x) => x.valid)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const result = {
      ClockIn: [],
      ClockOut: [],
      BreakIn: [],
      BreakOut: [],
    };

    normalized.forEach((it) => {
      const hh = String(it.date.getHours()).padStart(2, '0');
      const mm = String(it.date.getMinutes()).padStart(2, '0');
      result[it.type].push(`${hh}:${mm}`);
    });

    return result;
  }, [selectedDate, clockingsByDate]);

  const getUiStatusLabel = (status) => {
    switch (status) {
      case 'present':
        return t('Present');
      case 'absent':
        return t('Absent');
      case 'leave':
        return t('Leave');
      case 'sick-leave':
        return t('Sick leave');
      case 'holiday':
        return t('Holiday');
      case 'half-day':
        return t('Half day');
      case 'late':
        return t('Late');
      case 'early-leave':
        return t('Early Clock out');
      default:
        return status ? String(status) : '';
    }
  };

  const getUiStatusBadgeClass = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800';
      case 'absent':
        return 'bg-red-100 text-red-800';
      case 'leave':
        return 'bg-orange-100 text-orange-800';
      case 'sick-leave':
        return 'bg-red-100 text-red-800';
      case 'holiday':
        return 'bg-gray-100 text-gray-800';
      case 'half-day':
        return 'bg-orange-100 text-orange-800';
      case 'late':
        return 'bg-yellow-100 text-yellow-800';
      case 'early-leave':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  if (!canViewTarget) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-card border border-border rounded-xl p-6">
            <h1 className="text-xl font-semibold text-foreground">{t('Not authorized')}</h1>
            <p className="text-sm text-muted-foreground mt-2">{t("You don't have access to view this user's attendance.")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageShell
      title={title}
      description={uiRole === 'manager' ? t('Monitor attendance and manage schedules') : t('Track your attendance and manage time-off requests')}
      icon={Calendar}
      accentColor="blue"
      maxWidth="1600px"
      stats={[
        { label: t('Attendance Rate'), value: `${stats?.attendanceRate}%`, color: 'text-green-600' },
        { label: t('Present Days'), value: stats?.presentDays },
        { label: t('Absent Days'), value: stats?.absentDays, color: 'text-orange-600' },
        { label: t('Half Days'), value: stats?.halfDays, color: 'text-blue-600' },
      ]}
    >
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Calendar and Upcoming Schedule */}
          <div className="lg:col-span-2 space-y-6">
            {/* Export Button and Calendar View */}
            <div className="bg-card border border-border rounded-xl">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground flex items-center">
                      <Icon name="Calendar" size={20} className={titleIconGapClass} />
                      {t('Attendance Calendar')}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('Visual overview of attendance patterns and scheduled time-off')}
                    </p>
                  </div>
                  <div>
                  <button
                        onClick={handleCorrectionRequest}
                        disabled={!canActOnTarget}
                        className={`w-full text-left px-3 py-2 text-sm bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground rounded-md transition-colors flex items-center ${inlineSpaceClass}`}
                      >
                        <Icon name="Edit3" size={14} />
                        <span>{t('Correction Request')}</span>
                      </button>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {monthLoading && monthDayKeys.length === 0 ? (
                  <CalendarSkeleton />
                ) : (
                  <AttendanceCalendar
                    attendanceData={attendanceData}
                    month={currentMonth}
                    onMonthChange={setCurrentMonth}
                    onDateSelect={handleDateSelect}
                    selectedDate={selectedDate}
                  />
                )}
                {monthLoading && monthDayKeys.length > 0 && (
                  <div className={`mt-4 inline-flex items-center text-sm text-muted-foreground ${inlineSpaceClass}`} aria-live="polite">
                    <Skeleton className="h-2 w-2 rounded-full" />
                    <span>{t('Loading month...')}</span>
                  </div>
                )}
                {monthError && (
                  <div className="mt-4 text-sm text-red-600">{monthError}</div>
                )}
              </div>
            </div>

            {/* Work Schedule (moved from timesheet/workSchedule.tsx) */}
            <div className="bg-card border border-border rounded-xl">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground flex items-center">
                  <Icon name="Calendar" size={20} className={titleIconGapClass} />
                  {uiRole === 'manager' ? t('Work Schedule') : t('My Work Schedule')}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {uiRole === 'manager' ? t('View scheduled shifts (week/month)') : t('Your scheduled shifts (week/month)')}
                </p>
              </div>
              <div className="p-6">
                <WorkScheduleCalendar userId={canViewTarget ? targetUserId : (viewerUserId || undefined)} />
              </div>
            </div>


          </div>

          {/* Right Column - Detailed Day View, Leave Requests, and Approval Status */}
          <div className="space-y-6">
            <DetailedDayViewPanel
              selectedDate={selectedDate}
              selectedDateDetails={selectedDateDetails}
              selectedDayClockingsByType={selectedDayClockingsByType}
              getUiStatusBadgeClass={getUiStatusBadgeClass}
              getUiStatusLabel={getUiStatusLabel}
            />

            {/* Leave Request Panel - Moved above Approval Status Tracking */}
            <LeaveRequestPanel
              userId={targetUserId}
              canRequest={isSelf}
              canApprove={isHr || isManagerOfTarget}
            />

            {/* Approval Status Tracking Section - Moved below Leave Request Panel */}
            {isSelf && (
              <div className="bg-card border border-border rounded-xl">
                <div className="p-6 border-b border-border">
                  <h3 className="text-lg font-semibold text-foreground flex items-center">
                    <Icon name="Clock3" size={18} className={titleIconGapClass} />
                    {t('Approval Status Tracking')}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('Track the status of your attendance correction requests')}
                  </p>
                </div>
                <div className="p-6">
                  {correctionsLoading && correctionRequests.length === 0 ? (
                    <div className="space-y-3" aria-busy="true" aria-live="polite">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className={`flex items-center ${rtl ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
                            <Skeleton className="w-3 h-3 rounded-full" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-40 rounded" />
                              <Skeleton className="h-3 w-56 rounded" />
                            </div>
                          </div>
                          <Skeleton className="h-5 w-20 rounded-full" />
                        </div>
                      ))}
                    </div>
                  ) : correctionRequests?.length > 0 ? (
                    <div className="space-y-3">
                      {correctionRequests.slice(0, 3).map((request) => (
                        <div key={request?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className={`flex items-center ${rtl ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
                            <div className={`w-3 h-3 rounded-full ${
                              request?.status === 'approved' ? 'bg-green-500' :
                              request?.status === 'rejected' ? 'bg-red-500' : 'bg-yellow-500'
                            }`}></div>
                            <div>
                              <div className="text-sm font-medium text-foreground">
                                {request?.type} - {new Date(request?.date)?.toLocaleDateString()}
                              </div>
                              <div className="text-xs text-muted-foreground">{request?.reason}</div>
                            </div>
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            request?.status === 'approved' ? 'bg-green-100 text-green-800' :
                            request?.status === 'rejected'? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {getCorrectionRequestStatusLabel(request?.status)}
                          </div>
                        </div>
                      ))}

                      {correctionRequests.length > 3 && (
                        <button
                          type="button"
                          onClick={() => navigate('/attendance/correction-requests')}
                          className={`w-full flex items-center justify-center ${inlineSpaceClass} px-3 py-2 text-sm font-medium text-primary border border-border rounded-lg hover:bg-muted transition-colors`}
                        >
                          <span>{t('View all ({{n}})', { n: correctionRequests.length })}</span>
                          <Icon name={rtl ? 'ChevronLeft' : 'ChevronRight'} size={14} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="CheckCircle2" size={48} className="mx-auto mb-3 opacity-50" />
                      <p>{t('No correction requests found')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default AttendanceManagement;